import { Spinner } from '@ps/ui';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from './store';

/**
 * Gate for signed-in screens.
 *
 * It waits for the stored session to be checked rather than assuming: showing
 * a login page to someone who is already signed in is as wrong as the reverse.
 * This is a convenience, not a security boundary — the API authorises every
 * request on its own.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const profileComplete = useAuthStore((state) => state.profileComplete);
  const restore = useAuthStore((state) => state.restore);
  const location = useLocation();

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') {
    return (
      <div className="flex justify-center py-16 text-primary">
        <Spinner size="lg" />
      </div>
    );
  }

  if (status === 'anonymous') {
    // Remember where they were headed, so sign-in returns them there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // A name is needed before booking; everything else can wait.
  if (!profileComplete && location.pathname !== '/register') {
    return <Navigate to="/register" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
